/**
 * BzaKanban — Post-Signatur-Prozesskette als Kanban mit SLA-Warnungen.
 * (Iter88zs) User-Wahl 1c + 2ok + 3c.
 *
 * Zeigt 6 Spalten (BzA beantragt → ... → Projektplanung fertig).
 * Karten mit farbigem Rand nach SLA-Status (grün/gelb/rot).
 * Oben: Counter für überfällige/warnende Projekte + Button "Aufgaben erzeugen".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, RefreshCw, FileText, MapPin, ListPlus, Loader2, ArrowLeft,
} from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import MobileStageBar, { useColumnSnap } from "@/components/pipeline/MobileStageBar";

const STATUS_COLORS = {
  ok:      "border-zinc-700 bg-zinc-900/70",
  warning: "border-amber-500/60 bg-amber-500/10",
  overdue: "border-red-500/60 bg-red-500/10",
};

const STATUS_LABELS = {
  ok: "Im Zeitplan",
  warning: "Bald fällig",
  overdue: "Überfällig",
};

const fmtEur = (n) => new Intl.NumberFormat("de-DE", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(n || 0));

export default function BzaKanban() {
  const [data, setData] = useState({ stages: [], columns: {}, counters: { overdue: 0, warning: 0, ok: 0 } });
  const [loading, setLoading] = useState(true);
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const snap = useColumnSnap();

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/bza-kanban");
      setData(data);
    } catch (e) { toast.error("Kanban konnte nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const createOverdueTasks = async () => {
    setCreatingTasks(true);
    try {
      const { data } = await api.post("/bza-kanban/refresh-overdue-tasks");
      toast.success(`${data.created} neue Aufgaben erzeugt (${data.overdue_total} überfällig gesamt)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setCreatingTasks(false); }
  };

  const columns = useMemo(() => {
    if (!filterOverdue) return data.columns;
    const out = {};
    for (const [k, cards] of Object.entries(data.columns)) {
      out[k] = cards.filter(c => c.sla_status === "overdue");
    }
    return out;
  }, [data.columns, filterOverdue]);

  return (
    <div className="p-4 sm:p-6" data-testid="bza-kanban-page">
      <Link
        to="/"
        data-testid="bza-kanban-back-dashboard"
        className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-[#00E575] mb-3 transition"
      >
        <ArrowLeft size={14} /> Zurück zum Dashboard
      </Link>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-2xl font-black text-zinc-100">BzA-Prozesskette</h1>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <span className="text-[12px] text-zinc-400">
            <b className="text-emerald-400">{data.counters.ok}</b> im Plan ·
            <b className="text-amber-400"> {data.counters.warning}</b> bald fällig ·
            <b className="text-red-400"> {data.counters.overdue}</b> überfällig
          </span>
          <button
            onClick={() => setFilterOverdue(v => !v)}
            data-testid="kanban-filter-overdue"
            className={`h-9 px-3 rounded text-[12px] font-bold ${filterOverdue ? "bg-red-500 text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-200"}`}
          >
            <AlertTriangle size={13} className="inline mr-1" />
            {filterOverdue ? "Alle zeigen" : "Nur überfällige"}
          </button>
          <button
            onClick={createOverdueTasks}
            disabled={creatingTasks || data.counters.overdue === 0}
            data-testid="kanban-create-tasks"
            className="h-9 px-3 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {creatingTasks ? <Loader2 className="animate-spin" size={13} /> : <ListPlus size={13} />}
            Aufgaben für Überfällige anlegen
          </button>
          <button
            onClick={load}
            data-testid="kanban-refresh"
            className="h-9 w-9 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center justify-center"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Lade …</div>
      ) : (
        <>
        <div className="-mx-4"><MobileStageBar
          items={data.stages.map((s) => ({ key: s.key, label: s.label, count: (columns[s.key] || []).length,
            warn: (columns[s.key] || []).some((c) => c.sla_status === "overdue") }))}
          active={snap.active} onPick={snap.scrollTo} testid="bza-stage-bar"
        /></div>
        <div ref={snap.boardRef} onScroll={snap.onScroll} className="overflow-x-auto pb-6 snap-x snap-mandatory sm:snap-none overscroll-x-contain" data-testid="bza-board">
          <div className="flex gap-3 min-w-max">
            {data.stages.map((stage, colIdx) => (
              <div
                key={stage.key}
                data-col-index={colIdx}
                className="w-[calc(100vw-2rem)] sm:w-72 shrink-0 snap-start"
                data-testid={`kanban-column-${stage.key}`}
              >
                <div className="rounded-t-lg bg-zinc-800/60 px-3 py-2.5 border-b border-zinc-700">
                  <div className="text-[12.5px] font-bold text-zinc-100">{stage.label}</div>
                  <div className="text-[10.5px] text-zinc-500 flex items-center gap-1.5">
                    <Clock size={10} /> SLA {stage.sla_days || "–"} Tage
                    <span className="ml-auto text-[11px] text-zinc-400">
                      {(columns[stage.key] || []).length}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 mt-2 min-h-[60vh]">
                  {(columns[stage.key] || []).map((card) => (
                    <Link
                      key={card.project_id}
                      to={`/projects/${card.project_id}`}
                      data-testid={`kanban-card-${card.project_id}`}
                      className={`block rounded-lg border p-2.5 hover:brightness-110 transition ${STATUS_COLORS[card.sla_status]}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-[12.5px] text-zinc-100 truncate">
                          {card.customer_name}
                        </div>
                        {card.sla_status === "overdue" && (
                          <AlertTriangle size={12} className="text-red-400 shrink-0" />
                        )}
                        {card.sla_status === "warning" && (
                          <Clock size={12} className="text-amber-400 shrink-0" />
                        )}
                      </div>
                      {card.customer_city && (
                        <div className="mt-0.5 text-[10.5px] text-zinc-500 flex items-center gap-1">
                          <MapPin size={9} /> {card.customer_city}
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">{fmtEur(card.total_gross)}</span>
                        {card.docs_count > 0 && (
                          <span className="text-blue-300 inline-flex items-center gap-1">
                            <FileText size={9} /> {card.docs_count}
                          </span>
                        )}
                      </div>
                      <div className={`mt-1.5 text-[10.5px] font-semibold ${
                        card.sla_status === "overdue" ? "text-red-300" :
                        card.sla_status === "warning" ? "text-amber-300" : "text-zinc-500"
                      }`}>
                        {card.days_in_stage} Tage · {STATUS_LABELS[card.sla_status]}
                        {card.sla_status === "overdue" && card.sla_days > 0 && (
                          <> · {card.days_in_stage - card.sla_days} Tage über SLA</>
                        )}
                      </div>
                    </Link>
                  ))}
                  {(columns[stage.key] || []).length === 0 && (
                    <div className="text-center text-[11px] text-zinc-600 py-6 border border-dashed border-zinc-800 rounded-lg">
                      Keine Projekte
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
